// Utility functions for workspace management

/**
 * Get the logo URL for a workspace based on its volume name
 * @param volumeName - The workspace volume name (e.g., "weldom", "gedimat", etc.)
 * @returns The URL to the workspace logo image
 */
export const getWorkspaceLogo = (volumeName: string): string => {
  if (!volumeName) return '';
  
  // Convert to lowercase to match filename convention
  const logoName = volumeName.toLowerCase();
  
  // Use the same pattern as BeOne: /images/iconesEnseignes/logo_{workspaceName}.png
  return `/images/iconesEnseignes/logo_${logoName}.png`;
};

/**
 * Get workspace initials as fallback when logo fails to load
 * @param name - The workspace name
 * @returns The initials (first letter of each word)
 */
export const getWorkspaceInitials = (name: string): string => {
  if (!name) return '';
  
  return name
    .split(' ')
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase()
    .substring(0, 2);
};

/**
 * Generate a consistent color for workspace avatar fallback
 * @param name - The workspace name
 * @returns A hex color string
 */
export const getWorkspaceColor = (name: string): string => {
  const colors = [
    '#1976d2', '#dc004e', '#9c27b0', '#673ab7', 
    '#3f51b5', '#2196f3', '#03a9f4', '#00bcd4',
    '#009688', '#4caf50', '#8bc34a', '#cddc39',
    '#ffeb3b', '#ffc107', '#ff9800', '#ff5722'
  ];
  
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  return colors[Math.abs(hash) % colors.length];
};

/**
 * Format workspace name for display (convert all caps to title case)
 * @param name - The workspace name
 * @returns Formatted name
 */
export const formatWorkspaceName = (name: string): string => {
  if (!name) return '';
  
  // If the name is entirely in uppercase and has more than 2 characters, format it
  if (name === name.toUpperCase() && name.length > 2) {
    return name
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
  
  return name;
};
